// components/common/Button.jsx
import React from 'react';
import { getButtonClasses, getButtonIconClasses, getButtonSpinnerClasses } from '../../config/theme.config';

/**
 * Standardized Button Component
 * 
 * @param {string} variant - Button style variant (primary, secondary, danger, success, info, warning, ghost, link, dark, outlinePrimary)
 * @param {string} size - Button size (xs, sm, md, lg, xl)
 * @param {boolean} iconOnly - Whether button contains only an icon (makes it square)
 * @param {React.ReactNode} leftIcon - Icon component to display on the left
 * @param {React.ReactNode} rightIcon - Icon component to display on the right
 * @param {boolean} loading - Show loading spinner
 * @param {boolean} disabled - Disable the button
 * @param {boolean} fullWidth - Make button full width
 * @param {function} onClick - Click handler
 * @param {string} type - Button type (button, submit, reset)
 * @param {string} className - Additional custom classes
 * @param {React.ReactNode} children - Button content
 */
export const Button = ({
  variant = 'primary',
  size = 'md',
  iconOnly = false,
  leftIcon = null,
  rightIcon = null,
  loading = false,
  disabled = false,
  fullWidth = false,
  onClick,
  type = 'button',
  className = '',
  children,
  ...props
}) => {
  const buttonClasses = getButtonClasses({
    variant,
    size,
    iconOnly,
    disabled,
    loading,
    fullWidth,
    className,
  });

  const iconClasses = getButtonIconClasses(size);
  const spinnerClasses = getButtonSpinnerClasses(size);

  // Clone icon elements with proper classes
  const renderIcon = (icon) => {
    if (!icon) return null;
    return React.cloneElement(icon, {
      className: `${iconClasses} ${icon.props.className || ''}`.trim(),
    });
  };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={buttonClasses}
      {...props}
    >
      {loading ? (
        <>
          <div className={spinnerClasses} />
          {!iconOnly && <span>Loading...</span>}
        </>
      ) : (
        <>
          {leftIcon && renderIcon(leftIcon)}
          {!iconOnly && children}
          {rightIcon && renderIcon(rightIcon)}
        </>
      )}
    </button>
  );
};

export default Button;